import {
  LayoutAuth,
  LayoutWidget,
  LayoutDefault,
  RouteWrapper,
  KeepAliveRouteWrapper,
  RefineryRouteWrapper,
} from '@/components/layouts'

import {RoleGroups} from '@/util/constants'

const {
  PRINCIPAL_OWNER_ROLE_IDS,
  SALES_MANAGER_ROLE_IDS
} = RoleGroups

import {
  authenticatedOnly,
  unauthenticatedOnly,
  passwordResetOnly,
  adminOnly,
  companyOnly,
  leadCompany,
  fullAccessCompanyOnly,
  authorizedTradersOnly,
  shipSecureEnabled,
  apiAccessOnly,
  hasMfaToken,
  hasMfaTokenOrAuthToken,
  nonEnhancedModeOnly,
  limitOrdersEnabledOnly,
  masterLotsEnabledOnly,
  preSettlementTermsPageOnly,
  bullionWalletAccountSummaryEnabledOnly,
  editableMasterLotOnly,
  permissionsByRole,
  isRefineryGuard,
  // needs re-accept guard for new terms
  vendorOrAdminOnly,
  requiresReportingAccess,
  cashAdvancesPageAccess,
} from './guards'

export const routes = () => [
  {
    path: '*',
    component: () => import('@/views/error/NotFound.vue'),
  },
  {
    path: '/feedback',
    component: LayoutAuth,
    meta: {
      title: 'Feedback',
    },
    hidden: true,
    redirect: '/auth/login',
    children: [
      {
        path: ':companyId/:orderId',
        name: 'feedback',
        meta: {
          title: 'Feedback',
        },
        component: () => import('@/views/Feedback.vue'),
      }
    ]
  },
  {
    path: '/user',
    component: LayoutDefault,
    meta: {
      title: 'User',
      guards: [authenticatedOnly],
    },
    redirect: '/user/settings',
    hidden: true,
  },
  {
    path: '/auth',
    component: LayoutAuth,
    meta: {
      title: 'Login',
    },
    redirect: '/auth/login',
    hidden: true,
    children: [
      {
        path: 'login',
        name: 'login',
        meta: {
          title: 'Login',
          guards: [unauthenticatedOnly],
        },
        component: () => import('@/views/auth/Login.vue'),
      },
      {
        path: 'mfa-verification',
        name: 'mfaVerification',
        meta: {
          title: 'mfaVerification',
          guards: [hasMfaToken],
        },
        component: () => import('@/views/auth/MfaVerification.vue'),
      },
      {
        path: 'enable-mfa',
        name: 'enableMfa',
        meta: {
          title: 'enableMfa',
          guards: [hasMfaTokenOrAuthToken],
        },
        component: () => import('@/views/auth/EnableMfa.vue'),
      },
      {
        path: 'signup',
        name: 'signup',
        meta: {
          title: 'Signup',
          guards: [unauthenticatedOnly],
        },
        component: () => import('@/views/company_application/Signup.vue'),
      },
      {
        path: 'passwordForgotten',
        name: 'passwordForgotten',
        meta: {
          title: 'Password Forgotten',
        },
        component: () => import('@/views/auth/PasswordForgotten.vue'),
      },
      {
        path: 'forcePasswordReset',
        name: 'forcePasswordReset',
        meta: {
          title: 'Force Password Reset',
          guards: [passwordResetOnly],
        },
        component: () => import('@/views/auth/ForcePasswordReset.vue'),
      },
    ],
  },
  {
    path: '/calls',
    name: 'calls',
    meta: {
      title: 'Call Recordings',
    },
    guards: [adminOnly],
    component: LayoutWidget,
    children: [
      {
        path: '/calls/:recordingId',
        name: 'call_recording',
        meta: {
          title: 'Call Recording',
        },
        props: true,
        guards: [adminOnly],
        component: () => import('@/views/admin/CallRecording.vue'),
      }
    ]
  },
  {
    path: '/terms',
    name: 'static_terms',
    meta: {
      title: 'Terms and Conditions',
    },
    component: () => import('@/views/static/TermsAndConditions.vue'),
  },
  {
    path: '/PSPTerms',
    name: 'static_psp_terms',
    meta: {
      title: 'Pre-Settlement Payment Program Terms',
    },
    component: () => import('@/views/static/PublicPreSettlementTerms.vue'),
  },
  {
    path: '/privacy-policy',
    name: 'static_privacy_policy',
    meta: {
      title: 'Privacy Policy',
    },
    component: () => import('@/views/static/PrivacyPolicy.vue'),
  },
  {
    path: '/ach_terms',
    name: 'static_ach_terms',
    meta: {
      title: 'ACH Authorization',
    },
    component: () => import('@/views/static/ACHAuthorization.vue'),
  },
  {
    path: '/orders/ship-secure/terms',
    name: 'static_shipsecure_terms',
    meta: {
      title: 'ShipSecure Terms & Conditions'
    },
    component: () => import('@/views/static/ShipSecureTermsAndConditions.vue'),
  },
  {
    path: '/404',
    name: '404',
    meta: {
      title: 'Not Found',
    },
    component: () => import('@/views/error/NotFound.vue'),
  },
  {
    path: '/500',
    name: '500',
    meta: {
      title: 'Server Error',
    },
    component: () => import('@/views/error/Error.vue'),
  },
  {
    path: '/',
    component: LayoutDefault,
    meta: {
      title: 'home',
      icon: '',
      navRoot: true,
    },
    redirect: () => {
      if (window.matchMedia('(max-width: 950px)').matches) {

        return '/trade';
      }
      return '/dashboard';
    },
    children: [
      {
        path: '/test',
        name: 'test',
        meta: {
          title: 'test',
          hiddenInMenu: true,
          guards: [adminOnly],
        },
        component: () => import('@/views/static/TestPage.vue'),
      },
      {
        path: '/test/settlement-breakdown',
        name: 'settlement-breakdown-test',
        meta: {
          title: 'Settlement Breakdown Test',
          hiddenInMenu: true,
          guards: [adminOnly],
        },
        component: () => import('@/views/test/SettlementBreakdownTest.vue'),
      },
      {
        path: '/test/master-lot-builder',
        name: 'master-lot-builder-test',
        meta: {
          title: 'Upstream Master Lot Builder (Mock)',
          hiddenInMenu: true,
          guards: [adminOnly],
        },
        component: () => import('@/views/test/MasterLotBuilderTest.vue'),
      },
      {
        path: '/dashboard',
        name: 'dashboard',
        meta: {
          title: 'dashboard',
          icon: 'mdi-view-dashboard',
          guards: [authenticatedOnly],
        },
        component: () => import('@/views/Dashboard.vue'),
      },
      {
        path: '/addresses',
        name: 'addresses',
        meta: {
          title: 'Address Book',
          hiddenInMenu: true,
          guards: [authenticatedOnly, companyOnly, fullAccessCompanyOnly],
        },
        component: () => import('@/views/company/Addresses.vue'),
      },
      {
        path: '/bank-info',
        name: 'bankInfo',
        meta: {
          title: 'Bank Info',
          hiddenInMenu: true,
          guards: [authenticatedOnly, companyOnly, fullAccessCompanyOnly],
        },
        component: () => import('@/views/company/BankInfos.vue'),
      },
      {
        path: '/trade',
        name: 'trade',
        component: () => import('@/views/catalog/ProductList.vue'),
        meta: {
          title: 'trade',
          icon: 'mdi-gold',
          guards: [
            authenticatedOnly,
            authorizedTradersOnly,
            fullAccessCompanyOnly,
          ],
          showCatalogNav: true,
        },
      },
      {
        path: '/trade/category/:categoryId',
        meta: {
          title: 'productDetail',
          hiddenInMenu: true,
          guards: [
            authenticatedOnly,
            authorizedTradersOnly,
            fullAccessCompanyOnly,
          ],
          showCatalogNav: true,
        },
        props: true,
        component: () => import('@/views/catalog/ProductList.vue'),
      },
      {
        path: '/trade/product/:productId',
        meta: {
          title: 'productDetail',
          hiddenInMenu: true,
          guards: [
            authenticatedOnly,
            authorizedTradersOnly,
            fullAccessCompanyOnly,
          ],
          showCatalogNav: true,
          /*transitionName: 'slide-x-transition'*/
        },
        props: true,
        component: () => import('@/views/catalog/ProductDetail.vue'),
      },
      {
        path: '/refinery',
        component: RefineryRouteWrapper,
        redirect: '/refinery/dashboard',
        meta: {
          title: 'refine',
          icon: 'mdi-flask',
          guards: [vendorOrAdminOnly],
        },
        children: [
          {
            path: '/refinery/enroll',
            name: 'refineryEnroll',
            meta: {
              title: 'enroll',
              icon: 'mdi-account-plus',
              guards: [authenticatedOnly, fullAccessCompanyOnly, isRefineryGuard(true)],
              separator: 'after',
            },
            component: () => import('@/views/refinery/RefineryLanding.vue'),
          },
          {
            path: '/refinery/enroll/start',
            name: 'refineryEnrollStart',
            meta: {
              title: 'enrollStart',
              hiddenInMenu: true,
              guards: [authenticatedOnly, fullAccessCompanyOnly, isRefineryGuard(true)],
            },
            component: () => import('@/views/refinery/RefineryRegistration.vue'),
          },
          {
            path: '/refinery/dashboard',
            name: 'refiningDashboard',
            meta: {
              title: 'dashboard',
              guards: [authenticatedOnly, fullAccessCompanyOnly, isRefineryGuard()],
            },
            component: () => import('@/views/refinery/RefiningDashboard.vue'),
          },
          {
            path: '/refinery/lots',
            name: 'refiningLots',
            meta: {
              title: 'refiningLots',
              guards: [authenticatedOnly, fullAccessCompanyOnly, isRefineryGuard()],
            },
            component: () => import('@/views/refinery/Lots.vue'),
          },
          {
            path: '/refinery/lots/new',
            name: 'createRefiningLot',
            meta: {
              title: 'createRefiningLot',
              icon: 'mdi-flask-plus',
              hiddenInMenu: true,
              guards: [authenticatedOnly, fullAccessCompanyOnly, isRefineryGuard()],
            },
            component: () => import('@/views/refinery/LotSubmission.vue'),
          },
          {
            path: '/account-balances',
            name: 'AccountBalances',
            meta: {
              title: 'accountBalances',
              guards: [authenticatedOnly, fullAccessCompanyOnly, isRefineryGuard()],
            },
            component: () => import('@/views/refinery/AccountBalances.vue'),
          },
          {
            path: '/account-balances/:metal',
            name: 'MetalWalletDetail',
            meta: {
              title: 'metalWalletDetail',
              hiddenInMenu: true,
              bypassRouterKey: true,
              guards: [authenticatedOnly, fullAccessCompanyOnly, isRefineryGuard(), bullionWalletAccountSummaryEnabledOnly],
            },
            props: true,
            component: () => import('@/views/refinery/MetalWalletDetail.vue'),
          },
          {
            path: '/refinery/contracts',
            name: 'Contracts',
            meta: {
              title: 'contracts',
              guards: [isRefineryGuard()],
            },
            component: () => import('@/views/refinery/Contracts.vue'),
          },
          {
            path: '/refinery/contracts/:id',
            name: 'ContractDetail',
            meta: {
              title: 'contracts',
              hiddenInMenu: true,
              guards: [isRefineryGuard()]
            },
            props: true,
            component: () => import('@/views/refinery/ContractDetails.vue')
          },
          {
            path: '/refinery/terms',
            name: 'refineryTerms',
            meta: {
              title: 'refiningTerms',
              guards: [authenticatedOnly, fullAccessCompanyOnly, isRefineryGuard()],
            },
            component: () => import('@/views/refinery/RefineryTerms.vue'),
          },
          // Admin routes
          {
            path: '/admin/refinery/lots',
            name: 'adminLotList',
            meta: { title: 'receiveRefiningLots', guards: [adminOnly], separator: 'before' },
            component: () => import('@/views/refinery/ReceiveRefiningLots.vue'),
          },
          {
            path: '/admin/refinery/lots/received',
            name: 'adminReceivedLots',
            meta: { title: 'receivedRefiningLots', guards: [adminOnly] },
            component: () => import('@/views/refinery/ReceivedRefiningLots.vue'),
          },
          {
            path: '/admin/refinery/assays',
            name: 'adminAssays',
            meta: { title: 'assays', guards: [adminOnly] },
            component: () => import('@/views/refinery/AdminAssays.vue'),
          },
          // Master Lot Builder routes (UCG-2210 production-shape demo).
          // All gated by [adminOnly, masterLotsEnabledOnly]. Edit route
          // additionally redirects to detail when status > draft.
          // The MASTER_LOTS_ENABLED flag is a real Configuration row — admins
          // flip it on per environment via the Releases page.
          {
            path: '/refinery/master-lots',
            name: 'masterLotsList',
            meta: {
              title: 'masterLots',
              icon: 'mdi-cube-outline',
              guards: [adminOnly, masterLotsEnabledOnly],
            },
            component: () => import('@/views/refinery/MasterLotsList.vue'),
          },
          {
            path: '/refinery/master-lots/new',
            name: 'createMasterLot',
            meta: {
              title: 'createMasterLot',
              hiddenInMenu: true,
              guards: [adminOnly, masterLotsEnabledOnly],
            },
            component: () => import('@/views/refinery/MasterLotCreate.vue'),
          },
          // Upstream refining contract management (UCG-2210
          // contract-management slice). All admin + flag-gated. Routes are
          // hidden from the global nav; entry point is the "Contracts"
          // header link on `MasterLotsList.vue`.
          {
            path: '/refinery/master-lots/contracts',
            name: 'contractsList',
            meta: {
              title: 'upstreamContracts',
              hiddenInMenu: true,
              guards: [adminOnly, masterLotsEnabledOnly],
            },
            component: () => import('@/views/refinery/UpstreamContractsList.vue'),
          },
          {
            path: '/refinery/master-lots/contracts/new',
            name: 'createUpstreamContract',
            meta: {
              title: 'createUpstreamContract',
              hiddenInMenu: true,
              guards: [adminOnly, masterLotsEnabledOnly],
            },
            component: () => import('@/views/refinery/UpstreamContractCreate.vue'),
          },
          {
            path: '/refinery/master-lots/contracts/:id/edit',
            name: 'editUpstreamContract',
            meta: {
              title: 'editUpstreamContract',
              hiddenInMenu: true,
              guards: [adminOnly, masterLotsEnabledOnly],
            },
            props: route => ({ contractId: route.params.id }),
            component: () => import('@/views/refinery/UpstreamContractEdit.vue'),
          },
          {
            path: '/refinery/master-lots/:id',
            name: 'masterLotDetail',
            meta: {
              title: 'masterLot',
              hiddenInMenu: true,
              guards: [adminOnly, masterLotsEnabledOnly],
            },
            props: route => ({ masterLotId: route.params.id }),
            component: () => import('@/views/refinery/MasterLotDetail.vue'),
          },
          {
            path: '/refinery/master-lots/:id/edit',
            name: 'editMasterLot',
            meta: {
              title: 'editMasterLot',
              hiddenInMenu: true,
              guards: [adminOnly, masterLotsEnabledOnly, editableMasterLotOnly],
            },
            props: route => ({ masterLotId: route.params.id }),
            component: () => import('@/views/refinery/MasterLotEdit.vue'),
          },
          {
            path: '/refinery/lots/:id',
            name: 'refiningLotDetail',
            meta: {
              title: 'refiningLot',
              hiddenInMenu: true,
              guards: [authenticatedOnly, fullAccessCompanyOnly, isRefineryGuard()],
            },
            props: route => ({ refiningLotId: route.params.id }),
            component: () => import('@/views/refinery/RefineryLot.vue'),
          },
          {
            path: '/refinery/lots/:refiningLotId/assay-entry/:lotAssayId?',
            name: 'refineryAssayEntry',
            meta: {
              title: 'Assay Entry',
              hiddenInMenu: true,
              guards: [adminOnly, isRefineryGuard()],
            },
            props: true,
            component: () => import('@/views/refinery/AssayEntryAccordion.vue')
          },
          {
            path: '/refinery/cash-advances',
            name: 'refineryCashAdvances',
            meta: {
              title: 'cashAdvances',
              guards: [
                authenticatedOnly,
                fullAccessCompanyOnly,
                isRefineryGuard(),
                cashAdvancesPageAccess,
              ],
            },
            component: () => import('@/views/refinery/CashAdvances.vue'),
          },
          {
            path: '/metal-transactions',
            name: 'MetalTransactions',
            meta: {
              title: 'metalTransactions',
              guards: [adminOnly, authenticatedOnly, fullAccessCompanyOnly, isRefineryGuard(), requiresReportingAccess],
            },
            component: () => import('@/views/refinery/MetalTransactions.vue'),
          },
          
          

        ],
      },
      {
        path: '/admin',
        component: RouteWrapper,
        redirect: '/admin/categories',
        meta: {
          title: 'admin',
          icon: 'mdi-account-lock',
          guards: [adminOnly],
        },
        children: [
          {
            path: '/company-application',
            name: 'adminApplication',
            meta: {
              title: 'application',
              guards: [authenticatedOnly, companyOnly],
            },
            component: () =>
              import('@/views/company_application/CompanyApplication.vue'),
          },
          {
            path: '/admin/alerts',
            meta: {
              title: 'adminAlerts',
            },
            component: () => import('@/views/admin/Alerts.vue'),
          },
          {
            path: '/admin/stock-alerts',
            meta: {
              title: 'adminStockAlerts',
            },
            component: () => import('@/views/admin/StockAlerts.vue'),
          },
          {
            path: '/admin/limit-orders',
            meta: {
              title: 'limitOrders',
            },
            props: {admin: true},
            component: () => import('@/views/order/LimitOrders.vue'),
          },
          {
            path: '/admin/categories',
            meta: {
              title: 'adminCategories',
            },
            component: () => import('@/views/admin/Categories.vue'),
          },
          {
            path: '/admin/configuration',
            meta: {
              title: 'configuration',
            },
            component: () => import('@/views/admin/Configuration.vue'),
          },
          {
            path: '/admin/releases',
            name: 'adminReleases',
            meta: {
              title: 'releases',
            },
            component: () => import('@/views/admin/Releases.vue'),
          },
          {
            path: '/admin/signup',
            name: 'adminSignup',
            meta: {
              title: 'adminSignup',
            },
            component: () => import('@/views/company_application/Signup.vue'),
          },
          {
            path: '/admin/suiteanalytics-connect',
            name: 'suiteAnalyticsConnect',
            meta: {
              title: 'suiteAnalyticsConnect',
            },
            component: () => import('@/views/admin/SuiteAnalyticsConnect.vue'),
          },
          {
            path: '/admin/online-now',
            meta: {
              title: 'onlineNow',
            },
            component: () => import('@/views/admin/OnlineNow.vue'),
          },
          {
            path: '/admin/admin-permissions',
            name: 'adminPermissions',
            meta: {
              title: 'adminPermissions',
            },
            component: () => import('@/views/admin/AdminPermissions.vue'),
          },
        ],
      },
      {
        path: '/company-application',
        name: 'companyApplication',
        meta: {
          title: 'application',
          guards: [authenticatedOnly, companyOnly, leadCompany],
        },
        component: () =>
          import('@/views/company_application/CompanyApplication.vue'),
      },
      {
        path: '/orders',
        component: KeepAliveRouteWrapper,
        redirect: '/orders/sales-orders',
        meta: {
          title: 'orders',
          icon: 'mdi-barcode',
          guards: [fullAccessCompanyOnly],
        },
        children: [
          {
            path: '/orders/invoices',
            name: 'invoices',
            meta: {
              title: 'invoices',
              guards: [authenticatedOnly, companyOnly, fullAccessCompanyOnly],
            },
            component: () => import('@/views/order/Invoices.vue'),
          },
          {
            path: '/orders/invoices/:invoiceId',
            name: 'invoiceDetail',
            meta: {
              title: 'invoiceDetail',
              hiddenInMenu: true,
              guards: [authenticatedOnly, companyOnly, fullAccessCompanyOnly],
            },
            props: true,
            component: () => import('@/views/order/InvoiceDetail.vue'),
          },
          {
            path: '/orders/sales-orders',
            meta: {
              title: 'salesOrders',
            },
            props: {type: 'so'},
            guards: [companyOnly, fullAccessCompanyOnly],
            component: () => import('@/views/order/Orders.vue'),
          },
          {
            path: '/orders/purchase-orders',
            meta: {
              title: 'purchaseOrders',
            },
            props: {type: 'po'},
            guards: [companyOnly, fullAccessCompanyOnly],
            component: () => import('@/views/order/Orders.vue'),
          },
          {
            path: '/orders/limit-orders',
            meta: {
              title: 'limitOrders',
              guards: [
                limitOrdersEnabledOnly,
                companyOnly,
                fullAccessCompanyOnly,
              ],
            },
            component: () => import('@/views/order/LimitOrders.vue'),
          },
        ],
      },
      {
        path: '/orders/confirmation/:type/:confirmationNumber',
        meta: {
          title: 'orderConfirmation',
          hiddenInMenu: true,
          guards: [companyOnly, fullAccessCompanyOnly],
        },
        props: true,
        component: () => import('@/views/order/Confirmation.vue'),
      },
      {
        path: '/orders/sales-orders/:salesOrderId',
        meta: {
          title: 'salesOrder',
          hiddenInMenu: true,
          guards: [companyOnly, fullAccessCompanyOnly],
        },
        props: true,
        component: () => import('@/views/order/SalesOrder.vue'),
      },
      {
        path: '/orders/sales-orders/:salesOrderId/retail',
        meta: {
          title: 'salesOrderRetail',
          hiddenInMenu: true,
          guards: [companyOnly, fullAccessCompanyOnly],
        },
        props: true,
        component: () => import('@/views/order/RetailSalesOrder.vue'),
      },
      {
        path: '/orders/purchase-orders/:purchaseOrderId',
        meta: {
          title: 'purchaseOrder',
          hiddenInMenu: true,
          guards: [companyOnly, fullAccessCompanyOnly],
        },
        props: true,
        component: () => import('@/views/order/PurchaseOrder.vue'),
      },
      {
        path: '/orders/purchase-orders/:purchaseOrderId/retail',
        meta: {
          title: 'purchaseOrderRetail',
          hiddenInMenu: true,
          guards: [companyOnly, fullAccessCompanyOnly],
        },
        props: true,
        component: () => import('@/views/order/RetailPurchaseOrder.vue'),
      },
      {
        path: '/orders/limit-orders/:limitOrderId',
        meta: {
          title: 'limitOrder',
          hiddenInMenu: true,
          guards: [limitOrdersEnabledOnly, companyOnly, fullAccessCompanyOnly],
        },
        props: true,
        component: () => import('@/views/order/LimitOrder.vue'),
      },
      {
        path: '/alerts',
        meta: {
          title: 'alerts',
          hiddenInMenu: true,
          bypassRouterKey: true,
        },
        component: () => import('@/views/alerts/Inbox.vue'),
        props: false,
      },
      {
        path: '/alerts/:alertId',
        meta: {
          title: 'alerts',
          hiddenInMenu: true,
          bypassRouterKey: true,
        },
        component: () => import('@/views/alerts/Inbox.vue'),
        props: true,
      },
      {
        path: '/charts',
        meta: {
          title: 'charts',
          guards: [authenticatedOnly, fullAccessCompanyOnly],
          icon: 'mdi-chart-line',
        },
        component: RouteWrapper,
        children: [
          {
            path: '/charts/gold',
            meta: {
              title: 'gold',
              guards: [authenticatedOnly, fullAccessCompanyOnly],
            },
            component: () => import('@/views/charts/Gold.vue'),
          },
          {
            path: '/charts/silver',
            meta: {
              title: 'silver',
              guards: [authenticatedOnly, fullAccessCompanyOnly],
            },
            component: () => import('@/views/charts/Silver.vue'),
          },
          {
            path: '/charts/platinum',
            meta: {
              title: 'platinum',
              guards: [authenticatedOnly, fullAccessCompanyOnly],
            },
            component: () => import('@/views/charts/Platinum.vue'),
          },
          {
            path: '/charts/palladium',
            meta: {
              title: 'palladium',
              guards: [authenticatedOnly, fullAccessCompanyOnly],
            },
            component: () => import('@/views/charts/Palladium.vue'),
          },
          {
            path: '/charts/gold-silver',
            meta: {
              title: 'goldSilver',
              guards: [authenticatedOnly, fullAccessCompanyOnly],
            },
            component: () => import('@/views/charts/GoldSilver.vue'),
          }
        ],
      },
      {
        path: '/resources',
        component: RouteWrapper,
        meta: {
          title: 'resources',
          icon: 'mdi-book-open-variant',
          guards: [authenticatedOnly, fullAccessCompanyOnly],
        },
        children: [
          {
            path: '/resources/internationalShipping',
            meta: {
              title: 'internationalShipping',
              guards: [companyOnly],
            },
            component: () => import('@/views/static/InternationalShipping.vue'),
          },
          {
            path: '/resources/equity-trust',
            meta: {
              title: 'equityTrust',
              guards: [companyOnly, nonEnhancedModeOnly, fullAccessCompanyOnly],
            },
            component: () => import('@/views/static/EquityTrust.vue'),
          },
          {
            path: '/orders/ship-secure',
            meta: {
              title: 'shipSecure',
              guards: [shipSecureEnabled],
            },
            component: () => import('@/views/order/ShipSecure.vue'),
          },
          {
            path: '/orders/ship-secure/guidelines',
            meta: {
              title: 'shipSecureGuidelines',
              hiddenInMenu: true,
              guards: [shipSecureEnabled],
            },
            component: () => import('@/views/static/ShipSecureGuidelines.vue'),
          },
          {
            path: '/resources/contact-us',
            meta: {
              title: 'contactUs',
              guards: [authenticatedOnly, fullAccessCompanyOnly],
            },
            component: () => import('@/views/static/ContactUs.vue'),
          },
          {
            path: '/resources/shipping-info',
            meta: {
              title: 'shippingInfo',
              guards: [authenticatedOnly, fullAccessCompanyOnly],
            },
            component: () => import('@/views/static/ShippingInfo.vue'),
          },
          {
            path: '/resources/pre-settlement-payment-program',
            meta: {
              title: 'preSettlementPaymentProgram',
              guards: [authenticatedOnly, fullAccessCompanyOnly, preSettlementTermsPageOnly],
            },
            component: () => import('@/views/static/PreSettlementTerms.vue'),
          },
          {
            path: '/resources/api-docs',
            meta: {
              title: 'apiDocs',
              guards: [apiAccessOnly],
            },
            component: () => import('@/views/static/ApiDocs.vue'),
          },
          {
            path: '/resources/api-faq',
            meta: {
              title: 'apiFaq',
              guards: [apiAccessOnly],
              hiddenInMenu: true,
            },
            component: () => import('@/views/static/ApiFaq.vue'),
          },
          {
            path: '/resources/api-v2-migration',
            meta: {
              title: 'apiDocs',
              guards: [apiAccessOnly],
              hiddenInMenu: true,
            },
            component: () => import('@/views/static/ApiDocsV2Migration.vue'),
          },
          {
            path: '/user/companies',
            meta: {
              title: 'adminCompanies',
            },
            component: () => import('@/views/user/Companies.vue'),
          },
          {
            name: 'wireAchInfo',
            path: '/wire+ach+info',
            meta: {
              title: 'wireACHInstructions',
            },
            component: () => import('@/components/WireACHInstructions.vue'),
          },
        ],
      },
      {
        path: '/user/settings',
        meta: {
          title: 'settings',
          icon: 'mdi-cog',
          guards: [fullAccessCompanyOnly],
        },
        component: () => import('@/views/user/UserSettings.vue'),
      },
      {
        path: '/company/users',
        name: 'Users/Roles',
        meta: {
          title: 'company_users_roles',
          hiddenInMenu: true,
          guards: [permissionsByRole(PRINCIPAL_OWNER_ROLE_IDS.concat(SALES_MANAGER_ROLE_IDS))]
        },
        component: () => import('@/views/company/Contacts.vue'),
      },
      {
        path: '/403',
        name: 'Forbidden',
        meta: {
          title: 'access_denied',
          hiddenInMenu: true,
        },
        component: () => import('@/views/error/Deny.vue'),
      },
    ],
  },
]
